import React, { useState, useEffect } from 'react';
import finalLogo from '../assets/images/final-logo.svg';
import emailVerifyBg from '../assets/images/confirm-your-email.webp';
import Button from '../components/shared/Button';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useLocation, useNavigate } from 'react-router-dom';
import Modal from '../components/modals/modal';
import { UserService } from '../services';
import EmailIcon from '../assets/icons/emailSentSuccess.svg';
import { Toast } from '../components/shared/Toast';

const styles = {
	section: 'flex flex-col lg:flex-row min-h-screen bg-bgColor',
	leftColumn:
		'w-full lg:w-1/2 flex items-center justify-center py-8 px-6 xl:pr-[243px] xl:pl-[150px]',
	imageStyle: 'h-12 w-[260px] mb-20 mx-auto lg:mx-0',
	mainHeading: 'text-left text-main leading-39 font-semibold text-dark-blue mb-3',
	subHeading: 'text-left text-lg leading-100 text-gray mb-[50px] font-medium',
	input:
		'w-12 h-12 text-center text-xl font-bold border-1p3 border-lightGrey rounded-10p md:h-16 md:w-16',
	codeHeading: 'text-left text-base leading-21 font-medium text-gray mb-[60px]',
	rightBgImage: 'hidden lg:block relative w-1/2 bg-cover bg-center',
	carouselText: 'text-carousel leading-130 font-semibold text-white'
};

const EmailVerifyPage = () => {
	const [code, setCode] = useState(Array(6).fill(''));
	// const [currentTextIndex, setCurrentTextIndex] = useState(0);
	const [isImageLoaded, setIsImageLoaded] = useState(false);
	const location = useLocation();
	const navigate = useNavigate();
	const [userEmail, setUserEmail] = useState('');
	const [error, setError] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [showEmailVerifyModal, setShowEmailVerifyModal] = useState(false);
	const [modalDescription] = useState('');
	const [resendEmail, setResendEmail] = useState(true); // Flag for resend in progress, starts true to kick off timer
	const [secondsLeft, setSecondsLeft] = useState(60); // Timer state
	const [modalHeading] = useState('Verification Code Sent');
	const [modalIcon] = useState(EmailIcon);
	const [modalButtonText] = useState('Got it');
	const [mode, setMode] = useState('signup');

	const carouselTexts = 'From your first word to the final format, WriteForm helps you craft clean, structured, and impactful documents — without the usual formatting struggle.';

	// useEffect(() => {
	// 	const interval = setInterval(() => {
	// 		setCurrentTextIndex((prevIndex) => (prevIndex + 1) % carouselTexts.length);
	// 	}, 5000); // Change text every 5 seconds

	// 	return () => clearInterval(interval);
	// }, [carouselTexts.length, userEmail]);

	// Effect to handle the countdown timer
	useEffect(() => {
		if (resendEmail && secondsLeft > 0) {
			const timerId = setTimeout(() => {
				setSecondsLeft((prev) => prev - 1);
			}, 1000);
			return () => clearTimeout(timerId);
		} else if (resendEmail && secondsLeft === 0) {
			// Timer finished, allow resending again
			setResendEmail(false);
		}
	}, [resendEmail, secondsLeft]);

	useEffect(() => {
		// Check if state exists and contains the email
		if (location.state && location.state.email) {
			setUserEmail(location.state.email);
			if (location.state.mode === 'forgot-password') {
				setMode('forgot-password');
			}
		} else {
			// If email is not found, redirect to signup page to ensure proper flow
			navigate('/signup', { replace: true });
		}
	}, [location.state, navigate]);

	useEffect(() => {
		const img = new Image();
		img.src = emailVerifyBg;
		img.onload = () => setIsImageLoaded(true);
	}, []);

	const handleCodeChange = (e, index) => {
		const value = e.target.value;
		const newCode = [...code];

		// If the value is empty, clear the current digit
		if (value === '') {
			newCode[index] = '';
			setCode(newCode);
			// No automatic focus change here for clearing, let user move manually or with backspace
			return;
		}

		// Ensure only a single numeric digit is processed
		const numericValue = value.replace(/[^0-9]/g, '').slice(0, 1);

		if (numericValue) {
			newCode[index] = numericValue;
			setCode(newCode);

			// Move focus to next input if not the last one
			if (index < 5) {
				document.getElementById(`code-input-${index + 1}`).focus();
			}
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setError('');
		setIsLoading(true);

		try {
			const joined = code.join('');
			const response =
				mode === 'forgot-password'
					? await UserService.verifyPasswordResetCode(userEmail, joined)
					: await UserService.verifyCode(userEmail, joined);

			if (response.status === 200) {
				if (mode === 'forgot-password') {
					navigate('/new-password', {
						state: { 
							email: userEmail, 
							verificationCode: joined,
							otpVerified: true // Success flag to indicate OTP verification was successful
						},
						replace: true
					});
				} else {
					// Show success toast and redirect to login (robust backend message handling)
					const message =
								(typeof response?.message === "string" && response.message.trim())
									? response.message
									: "The account created successfully, Welcome to WriteForm.";

								Toast.success(message);
								navigate("/login", { replace: true });

				}
			} else {
				setResendEmail(false);
				setSecondsLeft(0); // Reset timer

				const message = response.message || "This code has expired. Request a new one." ;
				Toast.error(message);
				console.error('Code verification failed:', response);
			}
		} catch (error) {
			setResendEmail(false);
			setSecondsLeft(0); // Reset timer

			console.error('Code verification error:', error);
			Toast.error('Network error. Please check your connection and try again.');
		} finally {
			setIsLoading(false);
		}
	};

	const handleResendCode = async (e) => {
		e.preventDefault();
		setError('');
		setIsLoading(true);
		setCode(Array(6).fill(''));
		setTimeout(() => {
			const first = document.getElementById('code-input-0');
			if (first) first.focus();
		}, 0);

		try {
			const response =
				mode === 'forgot-password'
					? await UserService.resendPasswordResetCode(userEmail)
					: await UserService.resendVerificationCode(userEmail);

			if (response.status === 200) {
				setResendEmail(true);
				setSecondsLeft(60); // Start from 60 seconds (for 01:00 display)
				Toast.success('A new code has been sent to your email.');
			} else {
				// If resend failed, stop the timer and allow retry
				setResendEmail(false);
				setSecondsLeft(0); // Reset timer

				const message = response.message || 'Failed to resend code. Please try again.';
				setError(message);
				console.error('Resend code failed:', response);
			}
		} catch (error) {
			// If resend failed due to network error, stop the timer and allow retry
			setResendEmail(false);
			setSecondsLeft(0); // Reset timer

			console.error('Resend code error:', error);
			setError('Network error. Please check your connection and try again.');
		} finally {
			setIsLoading(false);
		}
	};

	const formatTime = (totalSeconds) => {
		const minutes = Math.floor(totalSeconds / 60);
		const seconds = totalSeconds % 60;
		return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
	};

	return (
		<div className={styles.section}>
			{/* Left Column (Email Verify Form) */}
			<div className={styles.leftColumn}>
				<div className="w-full max-w-md lg:w-[520px] lg:h-[645px]">
					<div className="mb-20">
						<svg
							className="cursor-pointer"
							width="40"
							height="40"
							viewBox="0 0 40 40"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
							onClick={() => {
								const state = location?.state || {};
								navigate('/signup', {
									state: {
										firstName: state.firstName || '',
										lastName: state.lastName || '',
										email: state.email || userEmail || ''
									}
								});
							}}
						>
							<path
								d="M19.0625 31.25L7.8125 20L19.0625 8.75M9.375 20H32.1875"
								stroke="#180A63"
								strokeWidth="3.75"
								strokeLinecap="round"
								strokeLinejoin="round"
							/>
						</svg>
					</div>
					<div>
						<img className={styles.imageStyle} src={finalLogo} alt="WriteForm Logo" />
						<h2 className={styles.mainHeading}>Verify your email</h2>
						<p className={styles.subHeading}>We’ve sent a 6-digit code to your email. Enter it below to continue.</p>
					</div>
					<form onSubmit={handleSubmit}>
						{error && (
							<div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
								{error}
							</div>
						)}
						<div className="flex justify-between gap-[12px] mb-5">
							{code.map((digit, index) => (
								<input
									key={index}
									id={`code-input-${index}`}
									type="text" // Changed to text to better control input for overwrite
									maxLength="1"
									value={digit}
									onChange={(e) => handleCodeChange(e, index)}
									onKeyDown={(e) => {
										// Allow navigation keys, backspace, delete, tab
										if (
											['ArrowLeft', 'ArrowRight', 'Backspace', 'Delete', 'Tab'].includes(e.key) ||
											((e.metaKey || e.ctrlKey) && ['a', 'c', 'v', 'x'].includes(e.key)) // Allow copy/paste/cut
										) {
											// Custom backspace logic: if current box is empty, move to previous and clear it
											if (e.key === 'Backspace' && index > 0 && code[index] === '') {
												document.getElementById(`code-input-${index - 1}`).focus();
												const newCode = [...code];
												newCode[index - 1] = ''; // Clear previous box
												setCode(newCode);
												e.preventDefault(); // Prevent default backspace action (which would clear the current empty box)
											}
											// For 'Delete' key, it will just clear the current box via onChange
											return; // Let these keys perform their default action
										}
										// Prevent input if not a number
										if (!/^[0-9]$/.test(e.key)) {
											e.preventDefault();
										}
									}}
									className={styles.input}
									disabled={isLoading}
								/>
							))}
						</div>

						<div className={styles.codeHeading}>
							{resendEmail ? (
								<>
									Didn’t get the code? You can resend in{' '}
									<span className="text-dark-blue">{formatTime(secondsLeft)}</span>{' '} seconds.
								</>
							) : (
								<>
									Didn't get a code?{' '}
									<span className="text-dark-blue cursor-pointer" onClick={handleResendCode}>
										Resend
									</span>
								</>
							)} 
						</div>

						<div>
							<Button
								type="submit"
								disabled={isLoading || code.some((digit) => digit === '')}
								onClick={handleSubmit}
							>
								{isLoading ? 'Verifying...' : 'Verify'}
							</Button>
						</div>
					</form>
				</div>
			</div>

			{/* Right Column (Image and Text) */}
			<div
				className={styles.rightBgImage}
				style={isImageLoaded ? { backgroundImage: `url(${emailVerifyBg})` } : {}}
			>
				{!isImageLoaded ? (
					<div className="absolute inset-0">
						<Skeleton
							height="100%"
							width="100%"
							baseColor="#E5E7EB"
							highlightColor="#F3F4F6"
							duration={1.2}
						/>
					</div>
				) : (
					<div className="absolute inset-0 bg-opacity-40 flex flex-col justify-end p-8">
						<div className="text-white bg-white/5 rounded-lg p-6 mx-4 backdrop-blur-30 border-2 border-white/10">
							{/* <div className="flex justify-start space-x-2 mb-4">
								{carouselTexts.map((_, index) => (
									<span
										key={index}
										className={`transition-all duration-300 ${
											index === currentTextIndex
												? 'h-2 w-8 bg-white rounded-full'
												: 'h-2 w-2 bg-white opacity-50 rounded-full'
										}`}
									></span>
								))}
							</div> */}
							<p className={styles.carouselText}>{carouselTexts}</p>
						</div>
					</div>
				)}
			</div>
			<Modal
				icon={<img src={modalIcon} alt="Modal Icon" className="mx-auto" />}
				isOpen={showEmailVerifyModal}
				heading={modalHeading}
				description={modalDescription}
				buttonText={modalButtonText}
				onButtonClick={() => {
					setShowEmailVerifyModal(false);
					if (modalButtonText === 'Login') {
						navigate('/login');
					}
				}}
			/>
		</div>
	);
};

export default EmailVerifyPage;
